import { useState, useMemo } from 'react'

export interface PaginationResult<T> {
  currentPageData: T[]
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  startIndex: number
  endIndex: number
  goToPage: (page: number) => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  setItemsPerPage: (itemsPerPage: number) => void
}

export const usePagination = <T>(
  data: T[], 
  initialItemsPerPage: number = 10
): PaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage)

  // Calculate pagination values
  const totalItems = data.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  
  // Ensure current page is within valid range
  const validCurrentPage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages))
  
  // Calculate start and end indices
  const startIndex = (validCurrentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  
  // Get current page data
  const currentPageData = useMemo(() => {
    if (itemsPerPage === -1) {
      // Show all items when "All" is selected
      return data
    }
    return data.slice(startIndex, endIndex)
  }, [data, startIndex, endIndex, itemsPerPage])

  // Navigation functions
  const goToPage = (page: number) => {
    const targetPage = Math.min(Math.max(1, page), totalPages)
    setCurrentPage(targetPage)
  }

  const goToNextPage = () => {
    if (validCurrentPage < totalPages) {
      setCurrentPage(validCurrentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (validCurrentPage > 1) {
      setCurrentPage(validCurrentPage - 1)
    }
  }

  const setItemsPerPage = (newItemsPerPage: number) => {
    setItemsPerPageState(newItemsPerPage)
    // Reset to first page when changing items per page
    setCurrentPage(1)
  }

  // Update current page if it becomes invalid due to data changes
  if (validCurrentPage !== currentPage) {
    setCurrentPage(validCurrentPage)
  }

  return {
    currentPageData,
    currentPage: validCurrentPage,
    totalPages: itemsPerPage === -1 ? 1 : totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage: itemsPerPage !== -1 && validCurrentPage < totalPages,
    hasPreviousPage: validCurrentPage > 1,
    startIndex: itemsPerPage === -1 ? 0 : startIndex,
    endIndex: itemsPerPage === -1 ? totalItems : endIndex,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    setItemsPerPage,
  }
}